import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn.js';

export type ToastTone = 'success' | 'error' | 'info';

type Toast = { id: number; tone: ToastTone; message: string };

type ToastApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const TONE_STYLES: Record<ToastTone, { className: string; Icon: typeof Info }> = {
  success: { className: 'border-success-600 bg-success-50 text-success-600', Icon: CheckCircle2 },
  error: { className: 'border-error-600 bg-error-50 text-error-600', Icon: AlertCircle },
  info: { className: 'border-info-600 bg-info-50 text-info-600', Icon: Info },
};

/** Xabar necha soniya turadi. Xato uzoqroq — uni o'qib ulgurish kerak. */
const DURATION: Record<ToastTone, number> = { success: 3000, info: 4000, error: 7000 };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, tone, message }]);
      setTimeout(() => dismiss(id), DURATION[tone]);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/**
       * `aria-live="polite"` — skrinrider xabarni o'qiydi, lekin foydalanuvchi
       * yozayotgan bo'lsa uni bo'lmaydi.
       */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2"
      >
        {toasts.map((toast) => {
          const { className, Icon } = TONE_STYLES[toast.tone];
          return (
            <div
              key={toast.id}
              className={cn(
                'pointer-events-auto flex items-start gap-2 border-l-4 bg-white px-4 py-3 text-sm',
                className,
              )}
            >
              <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
              <p className="flex-1 text-n-800">{toast.message}</p>
              <button
                type="button"
                aria-label="Yopish"
                onClick={() => dismiss(toast.id)}
                className="text-n-400 hover:text-n-600"
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast faqat <ToastProvider> ichida ishlaydi');
  return context;
}
